/**
 * MfaEnrollmentPanel — TOTP force-enrollment ceremony for mandatory-MFA users.
 *
 * SCRUM-57 follow-up to Codex review #2. After `/auth/login` returns
 * `mfa_enrollment_required`, this panel runs the three-step enrollment
 * ceremony defined by ADR 0018 D12:
 *
 *   1. POST /auth/mfa/enroll/totp
 *      body: { enrollmentToken }
 *      → 200 { otpauthUrl, secretBase32 }
 *      Auto-fired on mount. Result feeds the QR + manual-secret display.
 *
 *   2. POST /auth/mfa/enroll/totp/confirm
 *      body: { enrollmentToken, code: 6-8 digits }
 *      → 200 { backupCodes: string[] }
 *      User scans the QR with an authenticator app and submits the first
 *      generated code. Backend verifies + issues 10 single-use Argon2id-
 *      hashed backup codes (per ADR 0018 D2).
 *
 *   3. POST /auth/mfa/enroll/complete
 *      body: { enrollmentToken }
 *      → 200 { status: 'authenticated', user } + Set-Cookie session
 *      Frontend gates this call behind an explicit user acknowledgement
 *      that backup codes were saved (ADR 0018 D12: "preventing the race
 *      where a user is logged in but has not recorded their codes").
 *
 * The enrollmentToken is a 15-min stateless JWT (ADR 0018 D12). If it
 * expires mid-flow (any 401 from the enroll endpoints), the panel switches
 * to an "expired" view with a single "Back to sign in" CTA — restarting
 * login is the only recovery path.
 *
 * Tokens-only colors. ≥ 44 px touch targets. aria-live status region.
 * The QR is rendered as inline SVG via `qrcode.react/QRCodeSVG` so JSDOM
 * can render it for axe assertions.
 */

import * as React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button, TextField } from '@sparta/ui-kit';
import { apiClient } from '../../shared/api-client.js';

interface Props {
  enrollmentToken: string;
  onSuccess: () => void;
  onCancel: () => void;
}

type Step =
  | { kind: 'starting' }
  | {
      kind: 'qr-shown';
      otpauthUrl: string;
      secretBase32: string;
    }
  | {
      kind: 'codes-shown';
      otpauthUrl: string;
      secretBase32: string;
      backupCodes: readonly string[];
    }
  | { kind: 'completing'; backupCodes: readonly string[] }
  | { kind: 'fatal'; message: string }
  | { kind: 'expired' };

type FormError =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string };

const TOTP_CODE_PATTERN = /^[0-9]{6,8}$/;

export function MfaEnrollmentPanel({
  enrollmentToken,
  onSuccess,
  onCancel,
}: Props): React.ReactElement {
  const [step, setStep] = React.useState<Step>({ kind: 'starting' });
  const [code, setCode] = React.useState('');
  const [confirmFormState, setConfirmFormState] = React.useState<FormError>({
    kind: 'idle',
  });
  const [acknowledgedCodes, setAcknowledgedCodes] = React.useState(false);

  // -------------------------------------------------------------------------
  // Step 1: start enrollment on mount.
  // -------------------------------------------------------------------------
  React.useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const { data, response } = await apiClient.POST(
          '/auth/mfa/enroll/totp',
          { body: { enrollmentToken } },
        );
        if (cancelled) return;
        if (response.ok && data) {
          // OpenAPI types this as `{ otpauthUrl: string; secretBase32: string }`.
          // Defensive narrow at runtime in case the contract drifts.
          const d = data as {
            otpauthUrl?: unknown;
            secretBase32?: unknown;
          };
          if (
            typeof d.otpauthUrl === 'string' &&
            typeof d.secretBase32 === 'string'
          ) {
            setStep({
              kind: 'qr-shown',
              otpauthUrl: d.otpauthUrl,
              secretBase32: d.secretBase32,
            });
            return;
          }
          setStep({
            kind: 'fatal',
            message:
              'Unexpected enrollment response from the server. Please try signing in again.',
          });
          return;
        }
        if (response.status === 401) {
          setStep({ kind: 'expired' });
          return;
        }
        if (response.status === 409) {
          // mfa_already_enrolled — the user actually has a confirmed factor;
          // they should have hit the challenge path, not enrollment. Restart.
          setStep({ kind: 'expired' });
          return;
        }
        setStep({
          kind: 'fatal',
          message:
            'We couldn\'t start MFA enrollment. Please try signing in again.',
        });
      } catch {
        if (!cancelled) {
          setStep({
            kind: 'fatal',
            message:
              'Network error while starting MFA enrollment. Check your connection and try signing in again.',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enrollmentToken]);

  // -------------------------------------------------------------------------
  // Step 2: confirm the first TOTP code.
  // -------------------------------------------------------------------------
  const onConfirmSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      if (step.kind !== 'qr-shown') return;

      const trimmed = code.trim();
      if (!TOTP_CODE_PATTERN.test(trimmed)) {
        setConfirmFormState({
          kind: 'error',
          message: 'Enter the 6-digit code from your authenticator app.',
        });
        return;
      }

      setConfirmFormState({ kind: 'submitting' });
      try {
        const { data, response } = await apiClient.POST(
          '/auth/mfa/enroll/totp/confirm',
          { body: { enrollmentToken, code: trimmed } },
        );
        if (response.ok && data) {
          const d = data as { backupCodes?: unknown };
          if (
            Array.isArray(d.backupCodes) &&
            d.backupCodes.every((c: unknown): c is string => typeof c === 'string')
          ) {
            setStep({
              kind: 'codes-shown',
              otpauthUrl: step.otpauthUrl,
              secretBase32: step.secretBase32,
              backupCodes: d.backupCodes as readonly string[],
            });
            setConfirmFormState({ kind: 'idle' });
            return;
          }
          setConfirmFormState({
            kind: 'error',
            message:
              'Unexpected response from the server. Please try the code again.',
          });
          return;
        }
        if (response.status === 400) {
          setConfirmFormState({
            kind: 'error',
            message:
              'That code didn\'t match. Codes rotate every 30 seconds — try the latest one.',
          });
          return;
        }
        if (response.status === 401) {
          setStep({ kind: 'expired' });
          return;
        }
        setConfirmFormState({
          kind: 'error',
          message: 'Something went wrong. Try the code again.',
        });
      } catch {
        setConfirmFormState({
          kind: 'error',
          message: 'Network error. Check your connection and try again.',
        });
      }
    },
    [code, enrollmentToken, step],
  );

  // -------------------------------------------------------------------------
  // Step 3: complete the ceremony (gated on user acknowledgement).
  // -------------------------------------------------------------------------
  const onComplete = React.useCallback(async (): Promise<void> => {
    if (step.kind !== 'codes-shown' || !acknowledgedCodes) return;
    setStep({ kind: 'completing', backupCodes: step.backupCodes });
    try {
      const { response } = await apiClient.POST(
        '/auth/mfa/enroll/complete',
        { body: { enrollmentToken } },
      );
      if (response.ok) {
        // Session cookie is set; caller (LoginPage) decides where to send
        // the user.
        onSuccess();
        return;
      }
      if (response.status === 401) {
        setStep({ kind: 'expired' });
        return;
      }
      setStep({
        kind: 'fatal',
        message:
          'We couldn\'t finalize MFA enrollment. Please try signing in again.',
      });
    } catch {
      setStep({
        kind: 'fatal',
        message:
          'Network error while finalizing enrollment. Check your connection and try again.',
      });
    }
  }, [acknowledgedCodes, enrollmentToken, onSuccess, step]);

  // -------------------------------------------------------------------------
  // Render branches.
  // -------------------------------------------------------------------------

  if (step.kind === 'expired') {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-labelledby="mfa-enroll-expired-heading"
        data-testid="mfa-enrollment-expired"
        className="rounded-2xl p-5"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <h2
          id="mfa-enroll-expired-heading"
          className="text-base font-bold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Enrollment expired
        </h2>
        <p
          className="mt-2 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Your enrollment window expired. Sign in again to start a fresh
          enrollment ceremony.
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="mt-4 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)', background: 'transparent' }}
        >
          ← Back to sign in
        </button>
      </section>
    );
  }

  if (step.kind === 'fatal') {
    return (
      <section
        role="alert"
        aria-live="polite"
        aria-labelledby="mfa-enroll-fatal-heading"
        data-testid="mfa-enrollment-fatal"
        className="rounded-2xl p-5"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <h2
          id="mfa-enroll-fatal-heading"
          className="text-base font-bold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Couldn&apos;t start enrollment
        </h2>
        <p
          className="mt-2 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          {step.message}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="mt-4 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{ color: 'var(--fg-brand)', background: 'transparent' }}
        >
          ← Back to sign in
        </button>
      </section>
    );
  }

  if (step.kind === 'starting') {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-labelledby="mfa-enroll-starting-heading"
        data-testid="mfa-enrollment-starting"
      >
        <h2
          id="mfa-enroll-starting-heading"
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px', color: 'var(--fg-primary)' }}
        >
          Setting up multi-factor authentication
        </h2>
        <p
          className="mt-2 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Generating your authenticator-app setup details…
        </p>
      </section>
    );
  }

  if (step.kind === 'codes-shown' || step.kind === 'completing') {
    const codes =
      step.kind === 'codes-shown' ? step.backupCodes : step.backupCodes;
    const completing = step.kind === 'completing';
    return (
      <section
        aria-labelledby="mfa-enroll-codes-heading"
        data-testid="mfa-enrollment-backup-codes"
      >
        <h2
          id="mfa-enroll-codes-heading"
          className="text-3xl font-bold"
          style={{ letterSpacing: '-0.6px', color: 'var(--fg-primary)' }}
        >
          Save your backup codes
        </h2>
        <p
          className="mb-4 mt-2 text-sm"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          Each code can be used <strong>once</strong>. Store them somewhere
          safe — a password manager, a printout, or another device. If you
          ever lose access to your authenticator app, these codes are how
          you sign back in.
        </p>

        <ul
          aria-label="Backup codes"
          className="grid grid-cols-1 gap-1 rounded-2xl p-4 sm:grid-cols-2"
          style={{
            background: 'var(--bg-sunken)',
            border: '1px solid var(--border-subtle)',
            fontFamily:
              '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
          }}
        >
          {codes.map((c) => (
            <li
              key={c}
              className="text-sm tracking-wider"
              style={{ color: 'var(--fg-primary)' }}
            >
              {c}
            </li>
          ))}
        </ul>

        <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={acknowledgedCodes}
            onChange={(e) => setAcknowledgedCodes(e.target.checked)}
            disabled={completing}
            className="mt-1 h-4 w-4"
            style={{ accentColor: '#34495e' }}
          />
          <span style={{ color: 'var(--fg-secondary)' }}>
            I have saved these backup codes in a safe place. I understand
            that without these codes I will not be able to sign in if I
            lose access to my authenticator.
          </span>
        </label>

        {/* SCRUM-101: composes the kit Button (primary, lg, full).
            The `disabled` state on the kit Button applies
            `pointer-events-none` + `opacity-50` via the kit's a11y
            baseline, replacing the previous hand-rolled disabled
            cursor/background inline styles. The acknowledged-codes
            gate stays as the consumer's `disabled` prop. */}
        <Button
          type="button"
          variant="primary"
          size="lg"
          full
          className="mt-5"
          onClick={onComplete}
          disabled={!acknowledgedCodes || completing}
          aria-busy={completing}
          aria-disabled={!acknowledgedCodes || completing}
          aria-label={
            completing ? 'Finishing setup' : 'Finish setup and continue'
          }
        >
          {completing ? 'Finishing…' : 'Finish setup and continue'}
        </Button>
      </section>
    );
  }

  // step.kind === 'qr-shown'
  return (
    <section
      aria-labelledby="mfa-enroll-qr-heading"
      data-testid="mfa-enrollment-qr"
    >
      <h2
        id="mfa-enroll-qr-heading"
        className="text-3xl font-bold"
        style={{ letterSpacing: '-0.6px', color: 'var(--fg-primary)' }}
      >
        Set up your authenticator
      </h2>
      <p
        className="mb-4 mt-2 text-sm"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        Scan the QR code with an authenticator app (1Password, Google
        Authenticator, Authy). Then enter the 6-digit code it generates.
      </p>

      <div
        className="flex flex-col items-center gap-3 rounded-2xl p-4"
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <div
          // The QR is decorative for sighted users — the manual secret
          // below is the screen-reader-friendly alternative.
          aria-hidden="true"
        >
          <QRCodeSVG
            value={step.otpauthUrl}
            size={180}
            level="M"
            // bg is the surface so it blends; fg is the primary text token.
            bgColor="#ffffff"
            fgColor="#34495e"
          />
        </div>
        <details className="w-full">
          <summary
            className="cursor-pointer text-[13px] font-semibold"
            style={{ color: 'var(--fg-brand)' }}
          >
            Can&apos;t scan? Enter the code manually
          </summary>
          <div className="mt-2">
            <span
              className="block text-[11px] font-bold uppercase"
              style={{
                letterSpacing: '1px',
                color: 'var(--fg-tertiary)',
              }}
            >
              Setup key
            </span>
            <code
              data-testid="mfa-enrollment-secret"
              className="mt-1 block break-all rounded-xl p-2 text-xs"
              style={{
                background: 'var(--bg-sunken)',
                color: 'var(--fg-primary)',
                fontFamily:
                  '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {step.secretBase32}
            </code>
          </div>
        </details>
      </div>

      <form onSubmit={onConfirmSubmit} className="mt-5" noValidate>
        {/* SCRUM-100: TOTP verification code field composes the kit
            TextField (size="md", monospace, OTP autocomplete). The
            external submit-error region below (the
            <div role="alert" aria-live="polite">) is preserved as-is
            per the SCRUM-100 scope decision — the kit's field-level
            error wiring is not used here because the MFA UX wants a
            polite-live submit-error region, not an assertive field
            paragraph. TextField receives aria-invalid +
            aria-describedby via prop spread (no `error` prop). */}
        <TextField
          label="Verification code"
          type="text"
          autoComplete="one-time-code"
          inputMode="numeric"
          spellCheck={false}
          autoCapitalize="off"
          placeholder="123 456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={confirmFormState.kind === 'submitting'}
          monospace
          aria-invalid={confirmFormState.kind === 'error'}
          aria-describedby={
            confirmFormState.kind === 'error' ? 'mfa-confirm-error' : undefined
          }
        />

        <div role="alert" aria-live="polite" className="mb-3 min-h-5">
          {confirmFormState.kind === 'error' ? (
            <p
              id="mfa-confirm-error"
              className="text-sm"
              style={{ color: '#c0392b' }}
            >
              {confirmFormState.message}
            </p>
          ) : null}
        </div>

        {/* SCRUM-101: composes the kit Button (primary, lg, full).
            Inline-style block (background/color + boxShadow) replaced
            by --bg-brand-driven kit styling. */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          full
          disabled={confirmFormState.kind === 'submitting'}
          aria-busy={confirmFormState.kind === 'submitting'}
          aria-disabled={confirmFormState.kind === 'submitting'}
          aria-label={
            confirmFormState.kind === 'submitting' ? 'Verifying' : 'Verify code'
          }
        >
          {confirmFormState.kind === 'submitting' ? 'Verifying…' : 'Verify code'}
        </Button>

        <button
          type="button"
          onClick={onCancel}
          className="mt-4 inline-flex min-h-touch items-center text-[13px] font-semibold no-underline"
          style={{
            color: 'var(--fg-tertiary)',
            background: 'transparent',
          }}
        >
          ← Try a different account
        </button>
      </form>
    </section>
  );
}
